import { c as _c } from "react/compiler-runtime";
import { NextPage } from 'next';
import { appWithTranslation, SSRConfig } from 'next-i18next/pages';
import type { AppContext, AppInitialProps, AppProps } from 'next/app';
import NextApp from 'next/app';
import { ReactElement, ReactNode } from 'react';
import { FontVariableEnum, interFont } from 'shared/constants/fonts';
import DashboardLayout from 'shared/layout/components/dashboard-layout';
import { RootProviders } from 'shared/providers/root-providers';
import nextI18NextConfig from '../../next-i18next.config';
import '../../styles/globals.css';
import { DeviceTypeEnum, MetaDataFromServerInterface } from 'shared/hooks/use-metadata-from-server';
import { getServerDeviceType } from 'shared/utils/device/get-server-device-type';

// import { VersionMonitor } from 'shared/components/version-monitor'

export type NextPageWithLayout<P = {}, R = P> = NextPage<PageProps> & {
  getLayout: (page: ReactElement<any>, browserTabTitle: PageProps['browserTabTitle']) => ReactNode;
  getSecondaryLayout: (page: ReactElement<P>) => ReactElement<R>;
};
export interface PageProps {
  browserTabTitle: string;
}
type AppPropsWithLayout = AppProps<PageProps> & MetaDataFromServerInterface & {
  pageProps: SSRConfig;
} & {
  Component: NextPageWithLayout;
};
const getDefaultPageLayout = (page: ReactElement<any>, browserTabTitle: PageProps['browserTabTitle']) => <DashboardLayout browserTabTitle={browserTabTitle}>{page}</DashboardLayout>;
function App(t0) {
  const $ = _c(13);
  const {
    Component,
    pageProps,
    hostOnServer,
    serverDeviceType
  } = t0;
  const getLayout = Component.getLayout ?? getDefaultPageLayout;
  const getSecondaryLayout = Component.getSecondaryLayout ?? _temp;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <style jsx={true} global={true}>{`
        :root {
          ${FontVariableEnum.Inter}: ${interFont.style.fontFamily};
        }
      `}</style>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== Component || $[2] !== pageProps) {
    t2 = <Component {...pageProps} />;
    $[1] = Component;
    $[2] = pageProps;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== getLayout || $[5] !== getSecondaryLayout || $[6] !== pageProps.browserTabTitle || $[7] !== t2) {
    t3 = getLayout(getSecondaryLayout(t2), pageProps.browserTabTitle);
    $[4] = getLayout;
    $[5] = getSecondaryLayout;
    $[6] = pageProps.browserTabTitle;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== hostOnServer || $[10] !== serverDeviceType || $[11] !== t3) {
    t4 = <>{t1}<RootProviders hostOnServer={hostOnServer} serverDeviceType={serverDeviceType}>{t3}</RootProviders></>;
    $[9] = hostOnServer;
    $[10] = serverDeviceType;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}
function _temp(page) {
  return page;
}
App.getInitialProps = async (context: AppContext): Promise<MetaDataFromServerInterface & AppInitialProps> => {
  const ctx = await NextApp.getInitialProps(context);
  const {
    req,
    res
  } = context.ctx;
  const hostOnServer = req?.headers?.host || null;
  const serverDeviceType = req ? getServerDeviceType(req.headers['user-agent']) : DeviceTypeEnum.Unknown;
  if (req && res && req.url) {
    if (res.statusCode === 404) {
      const accept = req.headers['accept'] || '';
      const isHtmlRequest = accept.includes('text/html');

      // Only redirect actual page requests, not Next.js data fetching
      if (isHtmlRequest) {
        context.ctx.res?.writeHead(302, {
          Location: '/dashboard/not-found'
        });
        context.ctx.res?.end();
      }
    }
  }
  return {
    ...ctx,
    hostOnServer,
    serverDeviceType
  };
};
export default appWithTranslation(App, nextI18NextConfig);